import React, { useEffect, useRef, useState } from 'react';
import { adminApi } from '../api';
import { ArrowsLeftRight, MagnifyingGlass, X, Check, Clock } from '@phosphor-icons/react';

const eur = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

function ClientePicker({ label, value, onChange, testid }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const tmr = useRef(null);

  const onType = (val) => {
    setQ(val); setOpen(true);
    clearTimeout(tmr.current);
    if (val.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    tmr.current = setTimeout(async () => {
      try { setResults(await adminApi.valesSearch(val.trim())); } catch { setResults([]); } finally { setLoading(false); }
    }, 300);
  };
  const pick = (c) => { onChange(c); setOpen(false); setQ(''); setResults([]); };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-[#0F1829] mb-1">{label}</label>
      {value ? (
        <div className="flex items-center justify-between border border-[#E7E2D6] rounded-lg px-3 py-2 bg-[#FAF8F2]" data-testid={`${testid}-selected`}>
          <div className="min-w-0">
            <div className="font-medium text-[#0F1829] truncate">{value.NomeCompleto} <span className="text-xs font-mono text-[#9CA3AF]">#{value.ID_Cliente}</span></div>
            <div className="text-xs text-[#6B7280] truncate">{value.Email || '—'} · Saldo vales: <b>{eur(value.Saldo ?? value.saldo)}</b></div>
          </div>
          <button onClick={() => onChange(null)} className="ml-2 text-[#9CA3AF] hover:text-[#0F1829]" data-testid={`${testid}-clear`}><X size={16} /></button>
        </div>
      ) : (
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input data-testid={`${testid}-input`} value={q} onChange={(e) => onType(e.target.value)} onFocus={() => setOpen(true)}
            placeholder="Nº, nome ou email…" className="w-full pl-9 pr-3 py-2 border border-[#E7E2D6] rounded-lg text-sm outline-none focus:border-[#0F1829]" />
          {open && (q.trim().length >= 2) && (
            <div className="absolute z-20 mt-1 w-full bg-white border border-[#E7E2D6] rounded-lg shadow-lg max-h-64 overflow-auto">
              {loading ? <div className="px-3 py-2 text-sm text-[#9CA3AF]">A procurar…</div>
                : results.length === 0 ? <div className="px-3 py-2 text-sm text-[#9CA3AF]">Sem resultados.</div>
                : results.map((c) => (
                  <button key={c.ID_Cliente} onClick={() => pick(c)} data-testid={`${testid}-opt-${c.ID_Cliente}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#F5F2EB] border-b border-[#F1EEE6] last:border-0">
                    <div className="text-sm font-medium text-[#0F1829]">{c.NomeCompleto} <span className="text-xs font-mono text-[#9CA3AF]">#{c.ID_Cliente}</span></div>
                    <div className="text-xs text-[#6B7280]">{c.Email || '—'} · Saldo: {eur(c.Saldo)}</div>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminVales() {
  const [origem, setOrigem] = useState(null);
  const [destino, setDestino] = useState(null);
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}
  const [hist, setHist] = useState([]);

  const loadHist = () => adminApi.valesHistorico().then(setHist).catch(() => setHist([]));
  useEffect(() => { loadHist(); }, []);

  const val = Number(valor || 0);
  const saldoOrigem = origem ? Number(origem.Saldo ?? origem.saldo ?? 0) : 0;
  const canSubmit = origem && destino && origem.ID_Cliente !== destino.ID_Cliente && val > 0 && val <= saldoOrigem;

  const doTransfer = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await adminApi.valesTransfer({ origem_id: origem.ID_Cliente, destino_id: destino.ID_Cliente, valor: val, motivo });
      setMsg({ type: 'ok', text: `Transferidos ${eur(r.valor)} de ${r.origem.NomeCompleto} para ${r.destino.NomeCompleto}. Novo saldo da origem: ${eur(r.origem.saldo)}; destino: ${eur(r.destino.saldo)}.` });
      setOrigem(null); setDestino(null); setValor(''); setMotivo(''); setConfirm(false);
      loadHist();
    } catch (e) {
      setMsg({ type: 'err', text: e.message || 'Erro ao transferir.' });
      setConfirm(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center gap-2 mb-1">
        <ArrowsLeftRight size={22} className="text-[#0F1829]" />
        <h1 className="text-2xl font-bold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>Transferir Vales</h1>
      </div>
      <p className="text-sm text-[#6B7280] mb-6">Transfere um valor em vales de um cliente para outro. O valor é retirado do saldo da origem e criado como vale novo no destino.</p>

      {msg && (
        <div data-testid="vales-msg" className={`mb-4 rounded-lg px-4 py-3 text-sm ${msg.type === 'ok' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {msg.text}
        </div>
      )}

      <div className="bg-white border border-[#F3F0E7] rounded-xl p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <ClientePicker label="Cliente de origem" value={origem} onChange={(c) => { setOrigem(c); setMsg(null); }} testid="vales-origem" />
          <ClientePicker label="Cliente de destino" value={destino} onChange={(c) => { setDestino(c); setMsg(null); }} testid="vales-destino" />
        </div>

        {origem && destino && origem.ID_Cliente === destino.ID_Cliente && (
          <p className="text-sm text-red-600">A origem e o destino têm de ser clientes diferentes.</p>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#0F1829] mb-1">Valor a transferir (€)</label>
            <input data-testid="vales-valor" type="number" min="0.01" step="0.01" value={valor} onChange={(e) => setValor(e.target.value)}
              className="w-full border border-[#E7E2D6] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0F1829]" placeholder="0.00" />
            {origem && (
              <p className={`text-xs mt-1 ${val > saldoOrigem ? 'text-red-600' : 'text-[#6B7280]'}`}>
                Saldo disponível na origem: {eur(saldoOrigem)}{val > saldoOrigem ? ' — valor excede o saldo' : ''}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F1829] mb-1">Motivo (opcional)</label>
            <input data-testid="vales-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}
              className="w-full border border-[#E7E2D6] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0F1829]" placeholder="Ex.: correção / oferta" />
          </div>
        </div>

        <button disabled={!canSubmit || busy} onClick={() => setConfirm(true)} data-testid="vales-transfer-btn"
          className="px-5 py-2.5 rounded-lg bg-[#0F1829] text-white text-sm font-semibold hover:bg-[#002480] disabled:opacity-40 flex items-center gap-2">
          <ArrowsLeftRight size={16} /> Transferir
        </button>
      </div>

      {/* Histórico */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-[#0F1829] mb-2 flex items-center gap-1.5"><Clock size={15} /> Últimas transferências</h2>
        <div className="bg-white border border-[#F3F0E7] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#978D78] border-b"><th className="p-3">Data</th><th>Origem</th><th>Destino</th><th className="text-right">Valor</th><th>Motivo</th></tr></thead>
            <tbody>
              {hist.length === 0 ? <tr><td colSpan={5} className="p-5 text-center text-[#978D78]">Ainda não há transferências.</td></tr>
                : hist.map((h) => (
                  <tr key={h.id} className="border-b border-[#F1EEE6]" data-testid={`vales-hist-${h.id}`}>
                    <td className="p-3 text-xs">{(h.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="text-xs">{h.origem_nome} <span className="text-[#9CA3AF]">#{h.origem_id}</span></td>
                    <td className="text-xs">{h.destino_nome} <span className="text-[#9CA3AF]">#{h.destino_id}</span></td>
                    <td className="text-right font-medium">{eur(h.valor)}</td>
                    <td className="text-xs text-[#6B7280]">{h.motivo || '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmação */}
      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="vales-confirm-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
            <h3 className="text-lg font-bold text-[#0F1829] mb-2">Confirmar transferência</h3>
            <p className="text-sm text-[#374151]">Vai transferir <b>{eur(val)}</b> em vales de:</p>
            <ul className="text-sm my-3 space-y-1">
              <li>• Origem: <b>{origem?.NomeCompleto}</b> <span className="text-[#9CA3AF]">#{origem?.ID_Cliente}</span></li>
              <li>• Destino: <b>{destino?.NomeCompleto}</b> <span className="text-[#9CA3AF]">#{destino?.ID_Cliente}</span></li>
            </ul>
            <p className="text-xs text-[#6B7280] mb-4">Esta operação é imediata e afeta os saldos reais dos clientes.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirm(false)} disabled={busy} className="px-4 py-2 text-sm border border-[#E7E2D6] rounded-lg hover:bg-[#F5F2EB]" data-testid="vales-confirm-cancel">Cancelar</button>
              <button onClick={doTransfer} disabled={busy} data-testid="vales-confirm-ok"
                className="px-4 py-2 text-sm bg-[#0F1829] text-white rounded-lg hover:bg-[#002480] flex items-center gap-1.5 disabled:opacity-50">
                <Check size={15} /> {busy ? 'A transferir…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
